import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Link as RouterLink, useLocation, useSearchParams } from "react-router-dom";
import { getSecondaryNav, isSecondaryNavCollapsed } from "../config/navigation";
import { ACCOUNT_PHASES } from "../config/accountPlaybooks";
import { useUserRole } from "../contexts/UserRoleContext";
import { useVisibilityFilters, isItemVisible } from "../contexts/VisibilityFiltersContext";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

export const SECONDARY_NAV_WIDTH = 200;

function SecondaryNavItem({ to, label, active, beta, ai, planned, externalUrl }) {
  // externalUrl (bv. Formaat omzetten in Flow) opent in een nieuw tabblad
  const linkProps = externalUrl
    ? { component: "a", href: externalUrl, target: "_blank", rel: "noopener noreferrer" }
    : { component: RouterLink, to };
  return (
    <Box
      {...linkProps}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        textDecoration: "none",
        mx: 1,
        px: 1.25,
        py: 0.6,
        borderRadius: "0.5rem",
        bgcolor: active ? "#fff" : "transparent",
        boxShadow: active ? "0 1px 3px rgba(45,51,53,0.06)" : "none",
        transition: "all 0.12s",
        "&:hover": {
          bgcolor: active ? "#fff" : "rgba(45,51,53,0.03)",
        },
      }}
    >
      {ai && (
        <Box sx={{
          fontSize: "0.5rem", fontWeight: 800, fontFamily: '"Inter", system-ui, sans-serif',
          color: "#526075", bgcolor: "#d5e3fd",
          px: 0.75, py: 0.15, borderRadius: "4px",
          textTransform: "uppercase", letterSpacing: "0.06em", lineHeight: 1.4,
          flexShrink: 0,
        }}>
          AI
        </Box>
      )}
      <Typography
        noWrap
        sx={{
          fontSize: "0.76rem",
          fontFamily: '"Inter", system-ui, sans-serif',
          fontWeight: active ? 700 : 500,
          color: planned ? "#c4c8ca" : active ? "#635983" : "#6b7280",
          letterSpacing: active ? "-0.01em" : 0,
          flex: 1,
        }}
      >
        {label}
      </Typography>
      {externalUrl && <OpenInNewIcon titleAccess="Opent in een nieuw tabblad" sx={{ fontSize: 12, color: "#adb3b5", flexShrink: 0 }} />}
      {planned && (
        <Box sx={{
          fontSize: "0.5rem", fontWeight: 800, fontFamily: '"Inter", system-ui, sans-serif',
          color: "#d97706", bgcolor: "#fef3c7",
          px: 0.75, py: 0.15, borderRadius: "4px",
          textTransform: "uppercase", letterSpacing: "0.06em", lineHeight: 1.4,
          flexShrink: 0,
        }}>
          Planned
        </Box>
      )}
      {beta && (
        <Box sx={{
          fontSize: "0.5rem", fontWeight: 800, fontFamily: '"Inter", system-ui, sans-serif',
          color: "#7c3aed", bgcolor: "#ede9fe",
          px: 0.75, py: 0.15, borderRadius: "4px",
          textTransform: "uppercase", letterSpacing: "0.06em", lineHeight: 1.4,
          flexShrink: 0,
        }}>
          Beta
        </Box>
      )}
    </Box>
  );
}

export default function SecondaryNav() {
  const location = useLocation();
  const { permissions } = useUserRole();
  const [searchParams] = useSearchParams();
  // Ingeklapt-status onthouden (localStorage) zodat het menu ingeklapt blijft
  // over pagina's/herladen heen zodra je 'm dichtklapt.
  const [manualCollapsed, setManualCollapsedState] = useState(() => {
    try { return window.localStorage.getItem('secondaryNavCollapsed') === '1'; } catch { return false; }
  });
  const setManualCollapsed = (v) => {
    setManualCollapsedState(v);
    try { window.localStorage.setItem('secondaryNavCollapsed', v ? '1' : '0'); } catch { /* ignore */ }
  };

  const accountWorkspaceMatch = location.pathname.match(/^\/accounts\/([^/]+)\/workspace$/);
  const isAccountWorkspace = !!accountWorkspaceMatch;
  const wsAccountId = accountWorkspaceMatch?.[1];
  const isAIHub = location.pathname === "/ai-hub";
  const activeTab = isAccountWorkspace ? searchParams.get("tab") || "overview" : null;
  const activePhase = (isAccountWorkspace || isAIHub) ? searchParams.get("phase") || "strategie" : null;

  const visibility = useVisibilityFilters();
  const rawNav = isAccountWorkspace
    ? {
        label: "Account",
        items: [
          { path: `/accounts/${wsAccountId}/workspace`, label: "Overzicht", tab: "overview" },
          { path: `/accounts/${wsAccountId}/workspace?tab=profiel`, label: "Klantprofiel", tab: "profiel" },
          { path: `/accounts/${wsAccountId}/workspace?tab=huisstijl`, label: "Huisstijl", tab: "huisstijl" },
          { path: `/accounts/${wsAccountId}/workspace?tab=databeleid`, label: "Databeleid", tab: "databeleid" },
          { path: `/accounts/${wsAccountId}/workspace?tab=playbook`, label: "AI Playbook", tab: "playbook", ai: true },
        ],
      }
    : isAIHub
      ? {
          label: "Fases",
          items: ACCOUNT_PHASES.map((phase) => ({
            path: `/ai-hub?phase=${phase.id}`,
            label: phase.label,
            phase: phase.id,
          })),
        }
    : getSecondaryNav(location.pathname, permissions);

  const nav = rawNav ? {
    ...rawNav,
    items: rawNav.items.filter(item => isItemVisible(item, visibility)),
  } : null;

  if (!nav || nav.items.length === 0) return null;

  const shouldCollapse = manualCollapsed;

  // Collapsed state — sticky chevron that doesn't scroll away
  if (shouldCollapse) {
    return (
      <Box sx={{
        width: 36, flexShrink: 0,
        borderRight: "1px solid rgba(45,51,53,0.06)",
        bgcolor: "#fbfbfc",
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
      }}>
        <Box sx={{ position: "sticky", top: 56 }}>
          <Tooltip title={`${nav.label} menu`} placement="right">
            <Box
              onClick={() => setManualCollapsed(false)}
              sx={{
                width: 28, height: 28, borderRadius: "0.5rem",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", transition: "all 0.15s",
                bgcolor: "rgba(99,89,131,0.06)",
                '&:hover': { bgcolor: "rgba(99,89,131,0.12)" },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 16, color: "#635983" }} />
            </Box>
          </Tooltip>
        </Box>
      </Box>
    );
  }

  // Expanded state
  return (
    <Box
      sx={{
        width: SECONDARY_NAV_WIDTH,
        flexShrink: 0,
        borderRight: "1px solid rgba(45,51,53,0.06)",
        bgcolor: "#fbfbfc",
        display: { xs: "none", md: "block" },
        position: "relative",
      }}
    >
      <Box sx={{ position: "sticky", top: 44, pt: 2, pb: 2, maxHeight: "calc(100vh - 44px)", overflowY: "auto" }}>
        {/* Section title + collapse button */}
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 2.25, pb: 1.25,
        }}>
          {isAccountWorkspace ? (
            <Box
              component={RouterLink}
              to="/accounts"
              sx={{
                display: "inline-flex", alignItems: "center", gap: 0.5,
                textDecoration: "none", cursor: "pointer",
                px: 0.75, py: 0.5, ml: -0.75, borderRadius: "0.4rem",
                color: "#556071",
                transition: "all 0.12s",
                "&:hover": { color: "#635983", bgcolor: "rgba(99,89,131,0.06)" },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 14 }} />
              <Typography sx={{
                fontFamily: '"Inter", system-ui, sans-serif',
                fontSize: "0.66rem", fontWeight: 700,
                textTransform: "uppercase", letterSpacing: "0.1em",
              }}>
                Alle accounts
              </Typography>
            </Box>
          ) : (
            <Typography sx={{
              fontSize: "0.72rem",
              fontFamily: '"Manrope", system-ui, sans-serif',
              fontWeight: 800,
              letterSpacing: "-0.02em",
              color: "#2d3335",
            }}>
              {nav.label}
            </Typography>
          )}
          <Tooltip title="Menu sluiten" placement="right">
            <IconButton size="small" onClick={() => setManualCollapsed(true)}
              sx={{ color: "#adb3b5", p: 0.25, '&:hover': { color: '#526075' } }}>
              <MenuOpenIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* When in account workspace, show the section label as a sub-header */}
        {isAccountWorkspace && (
          <Typography sx={{
            fontFamily: '"Manrope", system-ui, sans-serif',
            fontSize: "0.72rem", fontWeight: 800, letterSpacing: "-0.02em",
            color: "#2d3335", px: 2.25, pb: 1.25,
          }}>
            {nav.label}
          </Typography>
        )}

        {/* Nav items */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.2 }}>
          {(() => {
            let lastGroup = null;
            return nav.items.map((item) => {
              const active = item.tab
                ? activeTab === item.tab && !item.phase
                : item.phase
                  ? activePhase === item.phase && activeTab === 'playbook'
                  : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);

              const groupLabel = item.group && item.group !== lastGroup ? item.group : null;
              if (item.group) lastGroup = item.group;

              const isBetaGroup = item.group === "Beta";

              return (
                <React.Fragment key={item.path}>
                  {groupLabel && (
                    <Typography
                      sx={{
                        fontSize: "0.54rem",
                        fontWeight: 700,
                        fontFamily: '"Inter", system-ui, sans-serif',
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                        color: isBetaGroup ? "#7c3aed" : "#adb3b5",
                        px: 2.25,
                        pt: 1.5,
                        pb: 0.35,
                      }}
                    >
                      {groupLabel}
                    </Typography>
                  )}
                  <SecondaryNavItem
                    to={item.path}
                    label={item.label}
                    active={active}
                    beta={item.beta}
                    ai={item.ai}
                    planned={item.planned}
                    externalUrl={item.externalUrl}
                  />
                </React.Fragment>
              );
            });
          })()}
        </Box>
      </Box>
    </Box>
  );
}
